// In-memory access token + silent refresh via the HttpOnly `lutrom_rt` cookie.
const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const BASE = `${RAW_BACKEND.replace(/\/+$/, '')}/api`;

export const IMP_KEY = 'lutrom_impersonation_stash';
export const USER_CACHE_KEY = 'lutrom_user';
export const LEGACY_TOKEN_KEY = 'lutrom_token';
const DEVICE_KEY = 'lutrom_device_rt';
const DEVICE_HEADER = 'X-Lutrom-Device-Token';

// iOS Home Screen apps don't reliably keep cookies across launches, so only
// there we also hold a separate rotating device credential.
export const isIosStandalone = () => {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios && (window.navigator.standalone === true
    || window.matchMedia?.('(display-mode: standalone)').matches === true);
};

export const deviceHeaders = () => {
  if (!isIosStandalone()) return {};
  const dt = localStorage.getItem(DEVICE_KEY);
  return { 'X-Lutrom-Standalone': '1', ...(dt ? { [DEVICE_HEADER]: dt } : {}) };
};

export const captureDeviceToken = (value) => {
  if (value && isIosStandalone()) localStorage.setItem(DEVICE_KEY, value);
};
export const AUTH_401_DETAILS = ['Not authenticated', 'Token expired', 'Invalid token', 'Session expired', 'Account disabled', 'User not found'];

const ACTIVE_WINDOW_MS = 15 * 60 * 1000;
const REFRESH_AHEAD_MS = 3 * 60 * 1000;

export class SessionEndedError extends Error {
  constructor(detail) {
    super(detail || 'Session expired');
    this.detail = detail || 'Session expired';
  }
}

let accessToken = null;
let expiresAt = 0;
let inflight = null;
let lastActivity = Date.now();
let sessionUserId = null;
const endListeners = new Set();

const decodeExp = (t) => {
  try {
    return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp * 1000;
  } catch {
    return 0;
  }
};

export const getAccessToken = () => accessToken;
export const setAccessToken = (t) => { accessToken = t || null; expiresAt = t ? decodeExp(t) : 0; };
export const setSessionUserId = (id) => { sessionUserId = id || null; };

export const readImpStash = () => {
  try {
    const v = JSON.parse(localStorage.getItem(IMP_KEY));
    return v?.targetId ? v : null;
  } catch { return null; }
};
export const readCachedUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_CACHE_KEY)) || null; } catch { return null; }
};
export const cacheUser = (u) => {
  try { if (u) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u)); } catch { /* private mode */ }
};
export const clearLocalSession = () => {
  setAccessToken(null);
  sessionUserId = null;
  [USER_CACHE_KEY, IMP_KEY, LEGACY_TOKEN_KEY, DEVICE_KEY].forEach((k) => localStorage.removeItem(k));
};

export const onSessionEnded = (fn) => { endListeners.add(fn); return () => endListeners.delete(fn); };
export const endSession = (detail) => {
  clearLocalSession();
  endListeners.forEach((fn) => fn(detail));
};

const postJson = async (path, { token, body } = {}) => {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...deviceHeaders(),
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  captureDeviceToken(res.headers.get(DEVICE_HEADER));
  const data = await res.json().catch(() => ({}));
  // Only an explicit server rejection ends the session; anything else
  // (network drop, 5xx, proxy/CDN 403) is treated as a temporary failure.
  if (res.status === 401 || (res.status === 403 && /disabled/i.test(data.detail || ''))) {
    throw new SessionEndedError(data.detail);
  }
  if (!res.ok) throw new Error(data.detail || `Refresh failed (${res.status})`);
  return data;
};

// Single-flight refresh. Throws SessionEndedError when the server says the
// session is gone; any other error (offline, 5xx) leaves local state intact.
export const refreshSession = () => {
  if (inflight) return inflight;
  inflight = (async () => {
    let { token, user } = await postJson('/auth/refresh');
    // Another tab signed in as someone else and replaced the shared cookie.
    if (sessionUserId && user.id !== sessionUserId) {
      localStorage.removeItem(IMP_KEY);
      cacheUser(user);
      window.location.assign('/dashboard');
      return new Promise(() => {});
    }
    sessionUserId = user.id;
    const imp = readImpStash();
    if (imp?.targetId) {
      try {
        ({ token, user } = await postJson(`/auth/impersonate/${imp.targetId}?renew=true`, { token }));
      } catch (e) {
        if (!(e instanceof SessionEndedError)) throw e;
        localStorage.removeItem(IMP_KEY);
      }
    }
    setAccessToken(token);
    cacheUser(user);
    return user;
  })().finally(() => { inflight = null; });
  return inflight;
};

export const upgradeLegacyToken = async (legacy) => {
  const { token, user } = await postJson('/auth/session/upgrade', { body: { token: legacy } });
  setAccessToken(token);
  setSessionUserId(user.id);
  cacheUser(user);
  return user;
};

export const serverLogout = () => postJson('/auth/logout').catch(() => {});

// Returns a usable access token, refreshing first if it is about to expire.
export const ensureAccessToken = async () => {
  if (!accessToken) return inflight ? inflight.then(() => accessToken, () => accessToken) : null;
  if (expiresAt - Date.now() > 60_000) return accessToken;
  try {
    await refreshSession();
  } catch (e) {
    if (e instanceof SessionEndedError) endSession(e.detail);
  }
  return accessToken;
};

// Proactive renewal while the user is active; idle tabs are left to lapse.
const markActive = () => { lastActivity = Date.now(); };
const maybeRenew = () => {
  if (!accessToken || !navigator.onLine || document.visibilityState !== 'visible') return;
  if (Date.now() - lastActivity > ACTIVE_WINDOW_MS) return;
  if (expiresAt - Date.now() > REFRESH_AHEAD_MS) return;
  refreshSession().catch((e) => { if (e instanceof SessionEndedError) endSession(e.detail); });
};

let started = false;
export const startSessionKeepAlive = () => {
  if (started) return;
  started = true;
  ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach((ev) =>
    window.addEventListener(ev, markActive, { passive: true, capture: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') { markActive(); maybeRenew(); }
  });
  setInterval(maybeRenew, 60_000);
};
